import React from 'react';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Button from '@/components/ui/Button';
import Row from 'react-bootstrap/Row';
import axios from 'axios';
import * as yup from 'yup';
import {Formik, FormikProps} from 'formik';
import {useStoreActions, useStoreState} from 'easy-peasy';
import Select from 'react-select';
import useData from '@/utils/useData';
import {buildURL} from '@/utils/urlBuilder';
import EvaluationTaskFsTextFormFields from '@/views/admin/evaluation_task_form_fieldsets/EvaluationTaskFsTextFormFields';
import EvaluationTaskCallFeaturesFormFields
    from '../evaluation_task_form_fieldsets/EvaluationTaskCallFeaturesFormFields';
import EvaluationTaskChatFeaturesFormFields
    from '../evaluation_task_form_fieldsets/EvaluationTaskChatFeaturesFormFields';
import EvaluationTaskServiceFeaturesFormFields
    from '../evaluation_task_form_fieldsets/EvaluationTaskServiceFeaturesFormFields';
import EvaluationTaskTranscriptionConfidenceFormFields
    from '../evaluation_task_form_fieldsets/EvaluationTaskTranscriptionConfidenceFormFields';
import EvaluationTaskRepetitiveCallsFormFields
    from '../evaluation_task_form_fieldsets/EvaluationTaskRepetitiveCallsFormFields';
import EvaluationTaskCallsPriceFormFields from '@/views/admin/evaluation_task_form_fieldsets/EvaluationTaskCallsPriceFormFields';
import CellLoader from '@/components/loading/CellLoader';
import {useAlert} from '@/components/alerts';
import i18n from 'i18n';

interface EvaluationTaskParams {
    key?: string;
    tag?: string;
    language?: string;
    partsToEvaluate?: string;
    url?: string;
    period?: number;
    pricePerMinute?: number;
}

interface EvaluationTaskFormValues {
    name: string;
    description: string;
    evaluationTaskTypeIdentifier: string;
    params: EvaluationTaskParams;
}

interface EvaluationTask {
    id: number;
    name: string;
    description: string;
    evaluationTaskTypeIdentifier: string;
    evaluationTaskTypeLabel?: string;
    params: EvaluationTaskParams;
}

interface ChannelDetails {
    eventTypeId: number;
}

interface EvaluationTaskTypeChoice {
    value: string;
    label: string;
}

interface ChannelEvaluationsTasksFormProps {
    objId?: number;
    activeChannelId?: string | number;
    onSave?: () => void;
}

const evaluationTaskFormSchemaFactory = (paramsSchema: any) => {
    return yup.object({
        name: yup.string()
            .max(100, i18n.t('jt_eval_name_max.text'))
            .required(i18n.t('jt_eval_name_required.text')),
        description: yup.string()
            .max(100, i18n.t('jt_eval_desc_max.text'))
            .required(i18n.t('jt_eval_desc_required.text')),
        evaluationTaskTypeIdentifier: yup.string().required(),
        params: yup.object().shape(paramsSchema),
    });
};

const evaluationTaskFormSchema = yup.lazy((values: EvaluationTaskFormValues) => {
    switch (values.evaluationTaskTypeIdentifier) {
        case 'evaluation_task_fs_text_params':
            return evaluationTaskFormSchemaFactory({
                tag: yup.string().required(i18n.t('jt_eval_tag_required.text')),
                key: yup.string().required(i18n.t('jt_eval_key_required.text')),
                language: yup.string().required(i18n.t('jt_eval_language_required.text')),
            });

        case 'evaluation_task_service_features_params':
            return evaluationTaskFormSchemaFactory({
                url: yup.string()
                    .url(i18n.t('jt_eval_url_invalid.text'))
                    .required(i18n.t('jt_eval_url_required.text')),
            });

        case 'evaluation_task_call_features_params':
            return evaluationTaskFormSchemaFactory({});
        case 'evaluation_task_chat_features_params':
            return evaluationTaskFormSchemaFactory({});
        case 'evaluation_task_transcription_confidence_params':
            return evaluationTaskFormSchemaFactory({});
        case 'evaluation_task_calls_price_params':
            return evaluationTaskFormSchemaFactory({
                pricePerMinute: yup.number()
                    .required(i18n.t('jt_eval_price_required.text'))
                    .min(0, i18n.t('jt_eval_price_min.text')),
            });
        case 'evaluation_task_repetitive_calls_params':
            return evaluationTaskFormSchemaFactory({
                period: yup.number()
                    .required(i18n.t('jt_eval_days_required.text'))
                    .min(0, i18n.t('jt_eval_days_min.text')),
            });
        default:
            return yup.object();
    }
});

const ChannelEvaluationsTasksForm: React.FC<ChannelEvaluationsTasksFormProps> = React.memo((
    {
        objId = 0,
        activeChannelId,
        onSave = () => { },
    },
) => {
    const {showSuccess} = useAlert();
    const channelId = activeChannelId ? Number(activeChannelId) || undefined : undefined;
    const evaluationTasksList = useStoreState((state: any) => state.channelEvaluationsTasks.items);
    const [evaluationTaskTypeChoices] = useData<EvaluationTaskTypeChoice[]>(
        channelId ? buildURL('evaluationTaskTypeChoicesResource', {obj_id: channelId}) : null,
        'evaluationTaskTypeChoices',
    );

    const addOrEditEvaluationTask = useStoreActions((actions: any) => actions.channelEvaluationsTasks.addOrEdit);
    const setSettingsModalOpen = useStoreActions((actions: any) => actions.settingsModalOpen.setSettingsModalOpen);

    let channelDetailUrl: string | null = null;
    if (channelId) {
        channelDetailUrl = buildURL('channelDetails', {channel_id: channelId});
    }
    const [channelDetails, loading] = useData<ChannelDetails>(channelDetailUrl, {
        useLocalStore: true,
        destroyOnUnmount: true,
    });

    const onSubmit = async (values: EvaluationTaskFormValues) => {
        let requestMethod: typeof axios.post | typeof axios.put;
        let url = buildURL('channelEvaluationsTasksResource', {obj_id: channelId || ''});
        if (objId) {
            url = `${url}/${objId}`;
            requestMethod = axios.put;
        } else {
            requestMethod = axios.post;
        }

        const response = await requestMethod(url, values);

        setSettingsModalOpen(false);
        addOrEditEvaluationTask(response.data);

        if (objId) {
            showSuccess(i18n.t('to_evaluation_ch.text'));
        } else {
            showSuccess(i18n.t('to_evaluation_cr.text'));
        }
        onSave();
    };

    let formContent: React.ReactNode;
    if (loading) {
        formContent = (<CellLoader fullHeight={true} />);
    } else {
        formContent = (
            <>
                <Formik<EvaluationTaskFormValues>
                    initialValues={evaluationTasksList.find((el: EvaluationTask) => el.id === objId) || {
                        name: '',
                        description: '',
                        evaluationTaskTypeIdentifier: evaluationTaskTypeChoices[0]?.value || '',
                        params: {
                            key: '',
                            tag: '',
                            language: '',
                            partsToEvaluate: '',
                            url: '',
                            period: 0,
                            pricePerMinute: 0,
                        },
                    }}
                    onSubmit={onSubmit}
                    validationSchema={evaluationTaskFormSchema}
                >
                    {(formik: FormikProps<EvaluationTaskFormValues>) => {
                        const selectedTypeOption = evaluationTaskTypeChoices.find(
                            (x) => x.value === formik.values.evaluationTaskTypeIdentifier,
                        );

                        const existingEvaluationTask = evaluationTasksList.find(
                            (el: EvaluationTask) => el.id === objId,
                        );

                        const fallbackTypeOption = selectedTypeOption
                            || (formik.values.evaluationTaskTypeIdentifier
                                ? {
                                    value: formik.values.evaluationTaskTypeIdentifier,
                                    label: existingEvaluationTask?.evaluationTaskTypeLabel
                                        || formik.values.evaluationTaskTypeIdentifier,
                                }
                                : null);

                        return (
                            <Form noValidate onSubmit={formik.handleSubmit}>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3" controlId="evaluationTaskTypeIdentifier" >
                                            <Form.Label>{i18n.t('jt_evaluation_task.text')}</Form.Label>
                                            <Select
                                                value={fallbackTypeOption}
                                                onChange={(v) => formik.setFieldValue('evaluationTaskTypeIdentifier', v?.value || '')}
                                                onBlur={formik.handleBlur}
                                                name="evaluationTaskTypeIdentifier"
                                                isSearchable={false}
                                                options={evaluationTaskTypeChoices}
                                                isDisabled={!!objId}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3" controlId="evaluationTaskName" >
                                            <Form.Label>{i18n.t('jt_name.text')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formik.values.name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={formik.touched.name && !!formik.errors.name}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formik.errors.name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3" controlId="evaluationTaskDescription" >
                                            <Form.Label>{i18n.t('jt_description.text')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="description"
                                                value={formik.values.description}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                isInvalid={formik.touched.description && !!formik.errors.description}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {formik.errors.description}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row >
                                {
                                    formik.values.evaluationTaskTypeIdentifier === 'evaluation_task_fs_text_params' ? (
                                        <EvaluationTaskFsTextFormFields formik={formik} channelId={channelId}
                                            channelEventTypeId={channelDetails?.eventTypeId} />
                                    ) : null
                                }

                                {
                                    formik.values.evaluationTaskTypeIdentifier === 'evaluation_task_service_features_params' ? (
                                        <EvaluationTaskServiceFeaturesFormFields formik={formik} />
                                    ) : null
                                }


                                {
                                    formik.values.evaluationTaskTypeIdentifier === 'evaluation_task_call_features_params' ? (
                                        <EvaluationTaskCallFeaturesFormFields formik={formik} />
                                    ) : null
                                }

                                {
                                    formik.values.evaluationTaskTypeIdentifier === 'evaluation_task_chat_features_params' ? (
                                        <EvaluationTaskChatFeaturesFormFields formik={formik} />
                                    ) : null
                                }

                                {
                                    formik.values.evaluationTaskTypeIdentifier
                                        === 'evaluation_task_transcription_confidence_params' ? (
                                        <EvaluationTaskTranscriptionConfidenceFormFields
                                            formik={formik}
                                        />
                                    ) : null
                                }


                                {
                                    formik.values.evaluationTaskTypeIdentifier === 'evaluation_task_calls_price_params' ? (
                                        <EvaluationTaskCallsPriceFormFields formik={formik} />
                                    ) : null
                                }

                                {
                                    formik.values.evaluationTaskTypeIdentifier === 'evaluation_task_repetitive_calls_params' ? (
                                        <EvaluationTaskRepetitiveCallsFormFields formik={formik} />
                                    ) : null
                                }

                                <Row>
                                    <Form.Group className="ml-auto mb-3" controlId="evaluation-task-save">
                                        <Button
                                            variant="info"
                                            fullWidth
                                            type="submit"
                                            data-cy="channel-evaluations-tasks-info-btn">
                                            {i18n.t('jt_save.text')}
                                        </Button>
                                    </Form.Group>
                                </Row>
                            </Form >
                        );
                    }}
                </Formik >
            </>
        );
    }

    return (
        <>{formContent}</>
    );
});

export default ChannelEvaluationsTasksForm; 